import type { HouseAdType } from "@yjsgames/house-ads";
import {
  createAdMediaElement,
  setMediaMuted,
  setMediaVolume,
  closeDelaySeconds,
  mediaHasSound,
} from "./houseAdMedia.js";
import type { AdFormat } from "@yjsgames/ad-ports";

export interface HouseAdOverlayConfig {
  readonly adType: HouseAdType;
  /** youtube embed / video mp4 / image png URL. */
  readonly mediaUrl: string;
  readonly format: AdFormat;
  /** 음소거 해제 시 적용할 광고 영상 볼륨 (0~100, houseAds.tsv 캠페인별 volume). 미지정 시 100(풀볼륨). */
  readonly mediaVolume?: number;
  /** 음소거 시작 여부 — 직전 광고에서 조절한 값을 이어받아 유지(ModalHost 세션 상태). 미지정 시 true(자동재생 정책). */
  readonly mutedInitial?: boolean;
  /** 음소거 토글 시 호출 — 호출자가 다음 광고에 유지하도록 새 muted 상태를 전파. */
  readonly onMutedChange?: (muted: boolean) => void;
  /** 광고 제목(헤드라인, i18n 적용 완료) — 하단 정보 카드에 굵게 표시. */
  readonly titleText: string;
  /** 광고 설명 카피(i18n 적용 완료) — 제목 아래 표시. 빈 문자열이면 설명 줄 생략. */
  readonly descText?: string;
  /** 스토어 이동 버튼 라벨 (i18n 적용 완료). */
  readonly storeText: string;
  /** 닫기 버튼 라벨 (i18n 적용 완료). */
  readonly closeText: string;
  /** 카운트다운 라벨 빌더 (남은 초 → "{sec}초 후 닫기"). */
  readonly countdownLabel: (sec: number) => string;
  /** 닫기(지연 후 클릭) — reward면 보상 지급, interstitial이면 단순 종료. 호출자 주입. */
  readonly onClose: () => void;
  /** 스토어 버튼 클릭 — 기기별 스토어 URL로 이동(window.open). */
  readonly onCtaClick: () => void;
}

const BTN_CSS =
  "padding:8px 14px;font-size:14px;border:0;border-radius:8px;background:rgba(0,0,0,0.62);color:#fff;cursor:pointer;";
const STORE_BTN_CSS =
  "padding:10px 20px;font-size:15px;font-weight:bold;border:0;border-radius:10px;background:#3a82f7;color:#fff;cursor:pointer;";

/**
 * 전체화면 보상형/전면 하우스 광고 오버레이 — 순수 DOM(Phaser 비의존).
 *
 * 화면 전체를 덮는 fixed 컨테이너에 미디어(iframe/video/img)를 깔고, 그 위에 **입력 차단 오버레이**를 덮어
 * 사용자가 영상을 멈추거나 컨트롤을 조작하지 못하게 한다. 닫기 버튼은 처음 숨겨두고
 * `closeDelaySeconds(format)`(reward 10초 / interstitial 5초) 강제 시청 후 노출한다.
 * 하단에 볼륨 토글(영상/youtube만)과 스토어 이동 버튼을 둔다. youtube 볼륨은 IFrame API postMessage로 제어한다.
 *
 * 생명주기: 생성자에서 DOM 부착 + 1초 카운트다운 타이머 시작, `destroy()`에서 타이머 정리 + DOM 제거.
 */
export class HouseAdOverlay {
  private readonly root: HTMLDivElement;
  private readonly media: HTMLVideoElement | HTMLIFrameElement | HTMLImageElement | null;
  private readonly closeBtn: HTMLButtonElement;
  private readonly countdownEl: HTMLDivElement;
  private remaining: number;
  private muted: boolean;
  private intervalId: ReturnType<typeof setInterval> | null = null;
  /** 직전 광고 unmute 유지용 — 자동재생(muted) 시작 후 player 준비를 기다렸다 음소거 해제하는 1회 타이머. */
  private initialUnmuteTimer: ReturnType<typeof setTimeout> | null = null;
  private closed = false;

  constructor(private readonly config: HouseAdOverlayConfig) {
    const doc = document;
    this.remaining = closeDelaySeconds(config.format);
    this.muted = config.mutedInitial ?? true; // 직전 광고에서 조절한 음소거 상태를 이어받음.

    const root = doc.createElement("div");
    root.setAttribute("data-house-ad-overlay", "");
    root.style.cssText =
      "position:fixed;inset:0;width:100vw;height:100vh;background:#000;z-index:100000;overflow:hidden;";
    this.root = root;

    // ── 미디어 + 입력 차단 오버레이 ──
    const media = createAdMediaElement(config.adType, config.mediaUrl);
    this.media = media;
    if (media) {
      media.style.cssText =
        "position:absolute;inset:0;width:100%;height:100%;border:0;object-fit:contain;background:#000;";
      root.appendChild(media);
      setMediaMuted(media, true); // 자동재생 정책 — muted 시작
      setMediaVolume(media, config.mediaVolume ?? 100); // 음소거 해제 시 풀볼륨 아닌 데이터 볼륨으로
      if (media instanceof HTMLVideoElement) {
        // jsdom은 play()가 undefined를 반환하므로 옵셔널 체이닝(브라우저는 Promise 반환).
        void media.play()?.catch(() => {
          /* 자동재생 차단 — 닫기 타이머는 그대로 진행 */
        });
      }
      // 직전 광고에서 음소거 해제 상태였으면 — 자동재생(muted)으로 시작한 뒤 음소거를 푼다.
      // youtube는 생성 직후 player 미준비라 postMessage가 유실될 수 있어 1초 후(준비 후) 적용한다(영상 볼륨 버튼이 동작하는 시점).
      if (!this.muted && mediaHasSound(config.adType)) {
        this.initialUnmuteTimer = setTimeout(() => {
          setMediaMuted(this.media, false);
          setMediaVolume(this.media, config.mediaVolume ?? 100);
        }, 1000);
      }
    }
    // 입력 차단: 미디어 전체를 덮는 투명 레이어 — 클릭/터치를 흡수해 영상 정지/컨트롤 조작 차단.
    const blocker = doc.createElement("div");
    blocker.setAttribute("data-input-blocker", "");
    blocker.style.cssText = "position:absolute;inset:0;z-index:1;background:transparent;";
    root.appendChild(blocker);

    // ── 상단 바: 카운트다운(좌) + 닫기(우, 처음 숨김) ──
    const topBar = doc.createElement("div");
    topBar.style.cssText =
      "position:absolute;top:0;left:0;right:0;display:flex;justify-content:space-between;align-items:center;padding:14px 16px;z-index:3;pointer-events:none;";
    this.countdownEl = doc.createElement("div");
    this.countdownEl.setAttribute("data-countdown", "");
    this.countdownEl.style.cssText =
      "color:#fff;font-size:14px;text-shadow:0 1px 3px #000;background:rgba(0,0,0,0.4);padding:4px 10px;border-radius:6px;";
    this.countdownEl.textContent = config.countdownLabel(this.remaining);
    this.closeBtn = doc.createElement("button");
    this.closeBtn.setAttribute("data-close", "");
    this.closeBtn.textContent = config.closeText;
    this.closeBtn.style.cssText = `display:none;pointer-events:auto;${BTN_CSS}`;
    this.closeBtn.addEventListener("click", () => this.handleClose());
    topBar.appendChild(this.countdownEl);
    topBar.appendChild(this.closeBtn);
    root.appendChild(topBar);

    // ── 정보 카드: 게임 제목(헤드라인) + 설명 카피 — 하단 바 위, 가독성 위해 그라데이션 backdrop ──
    const info = doc.createElement("div");
    info.setAttribute("data-ad-info", "");
    info.style.cssText =
      "position:absolute;left:0;right:0;bottom:72px;padding:14px 16px 16px;z-index:2;pointer-events:none;background:linear-gradient(transparent,rgba(0,0,0,0.6));";
    const titleEl = doc.createElement("div");
    titleEl.setAttribute("data-ad-title", "");
    titleEl.textContent = config.titleText;
    titleEl.style.cssText =
      "color:#fff;font-size:18px;font-weight:bold;text-shadow:0 1px 4px #000;line-height:1.3;";
    info.appendChild(titleEl);
    if (config.descText) {
      const descEl = doc.createElement("div");
      descEl.setAttribute("data-ad-desc", "");
      descEl.textContent = config.descText;
      descEl.style.cssText =
        "color:rgba(255,255,255,0.92);font-size:13px;text-shadow:0 1px 3px #000;line-height:1.4;margin-top:5px;";
      info.appendChild(descEl);
    }
    root.appendChild(info);

    // ── 하단 바: 볼륨 토글(사운드 있을 때) + 스토어 이동 ──
    const bottomBar = doc.createElement("div");
    bottomBar.style.cssText =
      "position:absolute;bottom:0;left:0;right:0;display:flex;justify-content:space-between;align-items:center;padding:16px;z-index:3;pointer-events:none;";
    if (mediaHasSound(config.adType)) {
      const volBtn = doc.createElement("button");
      volBtn.setAttribute("data-volume", "");
      volBtn.setAttribute("aria-label", "volume");
      volBtn.textContent = this.muted ? "🔇" : "🔊"; // 직전 광고 상태 반영.
      volBtn.style.cssText = `pointer-events:auto;${BTN_CSS}font-size:18px;`;
      volBtn.addEventListener("click", () => {
        // 사용자가 직접 조작 — 자동 해제 예약 타이머와 경합하지 않도록 취소.
        if (this.initialUnmuteTimer !== null) {
          clearTimeout(this.initialUnmuteTimer);
          this.initialUnmuteTimer = null;
        }
        this.muted = !this.muted;
        setMediaMuted(this.media, this.muted);
        // 해제 시 데이터 지정 볼륨 재적용 (youtube는 unMute 후 setVolume이라야 반영).
        if (!this.muted) setMediaVolume(this.media, config.mediaVolume ?? 100);
        volBtn.textContent = this.muted ? "🔇" : "🔊";
        config.onMutedChange?.(this.muted); // 다음 광고에 유지되도록 전파.
      });
      bottomBar.appendChild(volBtn);
    } else {
      bottomBar.appendChild(doc.createElement("div")); // 좌측 spacer (스토어 우측 정렬 유지)
    }
    const storeBtn = doc.createElement("button");
    storeBtn.setAttribute("data-store", "");
    storeBtn.textContent = config.storeText;
    storeBtn.style.cssText = `pointer-events:auto;${STORE_BTN_CSS}`;
    storeBtn.addEventListener("click", () => config.onCtaClick());
    bottomBar.appendChild(storeBtn);
    root.appendChild(bottomBar);

    doc.body.appendChild(root);

    // 강제 시청 카운트다운 → 닫기 노출.
    if (this.remaining <= 0) {
      this.revealClose();
    } else {
      this.intervalId = setInterval(() => {
        this.remaining -= 1;
        if (this.remaining <= 0) this.revealClose();
        else this.countdownEl.textContent = config.countdownLabel(this.remaining);
      }, 1000);
    }
  }

  private revealClose(): void {
    this.remaining = 0;
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.countdownEl.style.display = "none";
    this.closeBtn.style.display = "";
  }

  private handleClose(): void {
    if (this.closed) return;
    this.closed = true;
    this.config.onClose();
    this.destroy();
  }

  /** 타이머 정리 + DOM 제거. 외부(ModalHost.closeHouseAd)에서도 호출 가능(멱등). */
  destroy(): void {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    if (this.initialUnmuteTimer !== null) {
      clearTimeout(this.initialUnmuteTimer);
      this.initialUnmuteTimer = null;
    }
    this.root.remove();
  }
}
